/* MASONTEX · Media Core — preload.js (flat asset path)
   Predictive image warming. Offscreen Image() only — no DOM layout.
   DOM dataset contract (msPrefetched) stays in oem-product-lite.js. */
(function (global) {
  'use strict';

  const api = (global.MasontexMediaCore = global.MasontexMediaCore || {});
  const preload = {};

  preload.createWarmCache = () => {
    const warmed = new Set();
    return {
      has: (url) => warmed.has(url),
      warm(url) {
        if (!url || warmed.has(url)) return;
        warmed.add(url);
        if (typeof Image !== 'undefined') {
          const image = new Image();
          image.decoding = 'async';
          image.fetchPriority = 'low';
          image.src = url;
        }
      },
      warmPayload(payload, seen) {
        if (!payload) return;
        if (payload.base && !seen.has(payload.base)) {
          seen.add(payload.base);
          this.warm(payload.base);
        }
        if (payload.overlay && !seen.has(payload.overlay)) {
          seen.add(payload.overlay);
          this.warm(payload.overlay);
        }
      },
      size: () => warmed.size
    };
  };

  /* Keep predictive warming bounded. Large transparent product renders can
     otherwise compete with the actively selected image on slower connections.
     Warm one nearby view; all other choices still load on selection. */
  preload.prefetch = (ctx, warmCache, payloadFn) => {
    const connection = typeof navigator !== 'undefined' ? navigator.connection : null;
    if (connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '')) return;
    const n = api.normalize;
    const seen = new Set();
    const state = ctx.state;
    const suggestions = [];
    const neighbors = (items, selected, count) => {
      const keys = items.map(n.itemKey).filter(Boolean);
      const index = Math.max(0, keys.indexOf(selected));
      return keys
        .filter((key) => key !== selected)
        .sort((a, b) => Math.abs(keys.indexOf(a) - index) - Math.abs(keys.indexOf(b) - index))
        .slice(0, count);
    };
    neighbors(ctx.lists.views, state.view, 1)
      .forEach((view) => suggestions.push([state.color, state.visor, state.structure, view]));
    suggestions.forEach((args) => warmCache.warmPayload(payloadFn(...args), seen));
  };

  preload.schedule = (run) => {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 800 });
    else setTimeout(run, 500);
  };

  /* State-aware prewarm scheduler: debounces repeated state changes and runs
     the warm once on idle. Reuses the same warm logic — no new URL coverage. */
  preload.createDebounced = (fn, delay = 500) => {
    let timer = null;
    let idleTimer = null;
    const run = () => {
      idleTimer = null;
      fn();
    };
    return {
      schedule() {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          timer = null;
          if (typeof requestIdleCallback === 'function') {
            idleTimer = requestIdleCallback(run, { timeout: 800 });
          } else {
            run();
          }
        }, delay);
      },
      cancel() {
        if (timer) clearTimeout(timer);
        if (idleTimer && typeof cancelIdleCallback === 'function') cancelIdleCallback(idleTimer);
        timer = null;
        idleTimer = null;
      }
    };
  };

  api.preload = preload;
})(typeof window !== 'undefined' ? window : globalThis);
